import React, { useState, useEffect } from "react";
import { useAuth } from "../components/AuthProvider";
import AppleSignInButton from "../components/AppleSignInButton";
import MobileAppPrompt from "../components/MobileAppPrompt";
import { isAppleDevice } from "../utils/deviceUtils";
import { isMobileDevice } from "../utils/mobileDetect";
import { colors, sizes } from "../theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import Footer from "../components/Footer";
import { Text } from "react-native";
import { QRCodeCanvas } from "qrcode.react";

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);
  const { signIn, signUp, signInWithApple, resetPassword } = useAuth();
  const showAppleButton = isAppleDevice();

  // Vérifier si l'utilisateur est sur mobile au chargement
  useEffect(() => {
    const isMobile = isMobileDevice();
    const hasSeenPrompt = sessionStorage.getItem("mobile_prompt_dismissed");

    if (isMobile && !hasSeenPrompt) {
      setShowMobilePrompt(true);
    }
  }, []);

  const handleContinueAnyway = () => {
    sessionStorage.setItem("mobile_prompt_dismissed", "true");
    setShowMobilePrompt(false);
  };

  // Si on doit afficher le prompt mobile, ne rien afficher d'autre
  if (showMobilePrompt) {
    return <MobileAppPrompt onContinueAnyway={handleContinueAnyway} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation côté client
    if (!isLogin && password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      setLoading(false);
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        await signIn(email, password);
      } else {
        //await signUp(email, password);
      }
    } catch (err: any) {
      // Afficher un message d'erreur user-friendly
      const errorMessage =
        err.message || "Une erreur est survenue. Veuillez réessayer.";
      setError(errorMessage);
      console.error(
        `❌ Erreur ${isLogin ? "de connexion" : "d'inscription"}:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError("");
    setAppleLoading(true);

    try {
      await signInWithApple();
    } catch (err: any) {
      // Afficher un message d'erreur user-friendly
      const errorMessage =
        err.message ||
        "Une erreur est survenue lors de la connexion avec Apple.";
      setError(errorMessage);
      console.error("❌ Erreur connexion Apple:", err);
    } finally {
      setAppleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResetLoading(true);
    setResetSuccess(false);

    if (!resetEmail.trim()) {
      setError("Veuillez entrer votre adresse email");
      setResetLoading(false);
      return;
    }

    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
    } catch (err: any) {
      const errorMessage =
        err.message || "Erreur lors de l'envoi de l'email de réinitialisation.";
      setError(errorMessage);
      console.error("❌ Erreur réinitialisation:", err);
    } finally {
      setResetLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      position: "relative" as const,
    },
    backgroundPattern: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zM10 30c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    card: {
      backgroundColor: colors.white,
      borderRadius: `${sizes.cardRadius * 2}px`,
      padding: "48px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 8px 25px rgba(0,0,0,0.1)",
      width: "100%",
      maxWidth: "450px",
      position: "relative" as const,
      zIndex: 1,
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255,255,255,0.2)",
    },
    title: {
      fontSize: `${sizes.h3}px`,
      fontWeight: "bold",
      color: colors.text,
      textAlign: "center" as const,
      marginBottom: "10px",
    },
    subtitle: {
      fontSize: `${sizes.p}px`,
      color: colors.gray,
      textAlign: "center" as const,
      marginBottom: "30px",
    },
    form: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "20px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    label: {
      fontSize: `${sizes.text}px`,
      fontWeight: "500",
      color: colors.text,
    },
    input: {
      height: `${sizes.inputHeight + 4}px`,
      padding: `0 ${sizes.inputPadding + 4}px`,
      border: `2px solid ${colors.light}`,
      borderRadius: `${sizes.inputRadius + 2}px`,
      fontSize: `${sizes.text}px`,
      outline: "none",
      transition: "all 0.3s ease",
      backgroundColor: "rgba(248, 249, 250, 0.8)",
    },
    inputFocus: {
      borderColor: colors.primary,
      backgroundColor: colors.white,
      boxShadow: `0 0 0 3px rgba(180, 132, 14, 0.1)`,
    },
    button: {
      height: `${sizes.inputHeight + 4}px`,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.warning} 100%)`,
      color: colors.white,
      border: "none",
      minWidth: "120px",
      borderRadius: `${sizes.buttonRadius + 2}px`,
      fontSize: `${sizes.p}px`,
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      opacity: loading ? 0.7 : 1,
      boxShadow: "0 4px 12px rgba(180, 132, 14, 0.3)",
    },
    buttonHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(180, 132, 14, 0.4)",
    },
    switchText: {
      textAlign: "center" as const,
      fontSize: `${sizes.text}px`,
      color: colors.gray,
      marginTop: "20px",
    },
    switchLink: {
      color: colors.primary,
      textDecoration: "none",
      fontWeight: "bold",
      cursor: "pointer",
    },
    error: {
      backgroundColor: "#fee",
      color: "#c00",
      padding: "16px",
      borderRadius: `${sizes.radius}px`,
      fontSize: `${sizes.text}px`,
      textAlign: "center" as const,
      border: "1px solid #c00",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      boxShadow: "0 2px 8px rgba(220, 38, 38, 0.2)",
      animation: "shake 0.5s ease",
    },
    errorIcon: {
      fontSize: "20px",
      flexShrink: 0,
    },
    logo: {
      textAlign: "center" as const,
      marginBottom: "40px",
    },
    logoIcon: {
      fontSize: "64px",
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.warning} 100%)`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      margin: "20px 0",
      color: colors.gray,
      fontSize: `${sizes.text}px`,
    },
    dividerLine: {
      flex: 1,
      height: "1px",
      backgroundColor: colors.light,
    },
    dividerText: {
      margin: "0 15px",
    },
    forgotPassword: {
      textAlign: "right" as const,
      marginTop: "8px",
      marginBottom: "16px",
    },
    forgotPasswordLink: {
      color: colors.primary,
      textDecoration: "none",
      fontSize: `${sizes.text - 1}px`,
      cursor: "pointer",
      fontWeight: "600",
      transition: "opacity 0.2s ease",
    },
    modal: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
      animation: "fadeIn 0.2s ease",
    },
    modalContent: {
      backgroundColor: colors.white,
      borderRadius: `${sizes.cardRadius * 1.5}px`,
      padding: "32px",
      width: "90%",
      maxWidth: "450px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      animation: "slideUp 0.3s ease",
    },
    modalTitle: {
      fontSize: `${sizes.h4}px`,
      fontWeight: "bold",
      color: colors.text,
      marginBottom: "16px",
      textAlign: "center" as const,
    },
    modalText: {
      fontSize: `${sizes.text}px`,
      color: colors.gray,
      marginBottom: "24px",
      textAlign: "center" as const,
      lineHeight: 1.6,
    },
    successMessage: {
      backgroundColor: "#d4edda",
      color: "#155724",
      padding: "16px",
      borderRadius: `${sizes.radius}px`,
      fontSize: `${sizes.text}px`,
      textAlign: "center" as const,
      border: "1px solid #c3e6cb",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      boxShadow: "0 2px 8px rgba(21, 87, 36, 0.2)",
    },
    modalButtons: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      marginTop: "24px",
    },
    cancelButton: {
      padding: "12px 24px",
      backgroundColor: colors.light,
      color: colors.text,
      border: "none",
      borderRadius: `${sizes.buttonRadius}px`,
      fontSize: `${sizes.text}px`,
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
  };

  return (
    <>
      {isLogin && (
        <>
          <style>
            {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
            20%, 40%, 60%, 80% { transform: translateX(8px); }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
          </style>
          <div style={styles.container}>
            <div style={styles.backgroundPattern}></div>
            <div style={styles.card}>
              <div style={styles.logo}>
                <div style={styles.logoIcon}>
                  <img
                    src="/logo.png"
                    alt="Logo"
                    style={{ width: "104px", height: "104px" }}
                  />
                </div>
                <h1 style={styles.title}>Property Assist</h1>
                <p style={styles.subtitle}>
                  {isLogin
                    ? "Connectez-vous à votre compte"
                    : "Créez votre compte"}
                </p>
              </div>

              {error && (
                <div style={styles.error}>
                  <span style={styles.errorIcon}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                  </span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={styles.input}
                    placeholder="votre@email.com"
                    required
                    onFocus={(e) =>
                      Object.assign(e.target.style, styles.inputFocus)
                    }
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.light;
                      e.target.style.backgroundColor =
                        "rgba(248, 249, 250, 0.8)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={styles.input}
                    placeholder="••••••••"
                    required
                    onFocus={(e) =>
                      Object.assign(e.target.style, styles.inputFocus)
                    }
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.light;
                      e.target.style.backgroundColor =
                        "rgba(248, 249, 250, 0.8)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {isLogin && (
                  <div style={styles.forgotPassword}>
                    <span
                      style={styles.forgotPasswordLink}
                      onClick={() => setShowForgotPasswordModal(true)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "0.7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                    >
                      Mot de passe oublié ?
                    </span>
                  </div>
                )}

                {!isLogin && (
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Confirmer le mot de passe
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={styles.input}
                      placeholder="••••••••"
                      required
                      onFocus={(e) =>
                        Object.assign(e.target.style, styles.inputFocus)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = colors.light;
                        e.target.style.backgroundColor =
                          "rgba(248, 249, 250, 0.8)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  style={styles.button}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      Object.assign(e.currentTarget.style, styles.buttonHover);
                    }
                  }}
                  onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, {
                      transform: "none",
                      boxShadow: "0 4px 12px rgba(180, 132, 14, 0.3)",
                    });
                  }}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Connexion...
                    </span>
                  ) : isLogin ? (
                    "Se connecter"
                  ) : (
                    "S'inscrire"
                  )}
                </button>
              </form>

              {/* Bouton Apple - affiché en priorité sur les appareils Apple */}
              {showAppleButton && (
                <>
                  <div style={styles.divider}>
                    <div style={styles.dividerLine}></div>
                    <span style={styles.dividerText}>ou</span>
                    <div style={styles.dividerLine}></div>
                  </div>

                  <AppleSignInButton
                    onClick={handleAppleSignIn}
                    loading={appleLoading}
                    text={
                      isLogin
                        ? "Se connecter avec Apple"
                        : "S'inscrire avec Apple"
                    }
                  />
                </>
              )}

              <p style={styles.switchText}>
                {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}{" "}
                <span
                  style={styles.switchLink}
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError("");
                    setEmail("");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                >
                  {isLogin ? "S'inscrire" : "Se connecter"}
                </span>
              </p>
            </div>
          </div>
        </>
      )}

      {!isLogin && (
        <>
          <style>
            {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}
          </style>
          <div style={styles.container}>
            <div style={styles.backgroundPattern}></div>
            <div style={{ ...styles.card, maxWidth: "750px" }}>
              <div style={styles.logo}>
                <div>
                  <img
                    src="/logo.png"
                    alt="Logo"
                    style={{ width: "104px", height: "104px" }}
                  />
                </div>
                <h1 style={styles.title}>Créer un compte</h1>
                <p style={{ ...styles.subtitle, fontSize: "16px", marginBottom: "32px" }}>
                  Inscription obligatoire via notre application Property Assist
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "32px",
                  marginBottom: "32px",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "stretch",
                }}
              >
                {/* QR Code iOS */}
                <div
                  style={{
                    flex: "1 1 280px",
                    minWidth: "280px",
                    maxWidth: "320px",
                    padding: "24px",
                    backgroundColor: "rgba(0,0,0, 0.03)",
                    borderRadius: "16px",
                    textAlign: "center",
                    border: "1px solid rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <h3 style={{ fontSize: "20px", fontWeight: "700", color: colors.text, marginBottom: "16px", display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    App Store
                  </h3>
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: '16px' }}>
                    <QRCodeCanvas
                      value={"https://apps.apple.com/be/app/property-assist/id6450917128"}
                      size={160}
                      bgColor={"#ffffff"}
                      fgColor={"#000000"}
                      level={"L"}
                      includeMargin={false}
                    />
                  </div>
                  <a href="https://apps.apple.com/be/app/property-assist/id6450917128" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: colors.primary, fontWeight: '600', fontSize: '14px' }}>
                    Ouvrir l'App Store
                  </a>
                </div>

                {/* QR Code Android */}
                <div
                  style={{
                    flex: "1 1 280px",
                    minWidth: "280px",
                    maxWidth: "320px",
                    padding: "24px",
                    backgroundColor: "rgba(0,0,0, 0.03)",
                    borderRadius: "16px",
                    textAlign: "center",
                    border: "1px solid rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <h3 style={{ fontSize: "20px", fontWeight: "700", color: colors.text, marginBottom: "16px", display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    Google Play
                  </h3>
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: '16px' }}>
                    <QRCodeCanvas
                      value={"https://play.google.com/store/apps/details?id=com.louisv2304.oosmoseproperties"}
                      size={160}
                      bgColor={"#ffffff"}
                      fgColor={"#000000"}
                      level={"L"}
                      includeMargin={false}
                    />
                  </div>
                  <a href="https://play.google.com/store/apps/details?id=com.louisv2304.oosmoseproperties" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: colors.primary, fontWeight: '600', fontSize: '14px' }}>
                    Ouvrir le Play Store
                  </a>
                </div>
              </div>

              <div style={{ textAlign: "center", marginTop: "40px", paddingTop: "24px", borderTop: `1px solid ${colors.light}` }}>
                <p style={{ fontSize: "15px", color: colors.gray, marginBottom: "16px" }}>
                  Vous avez déjà un compte ?
                </p>
                <button
                  onClick={() => {
                    setIsLogin(true);
                    setError("");
                    setEmail("");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                  style={{
                    ...styles.button,
                    background: "transparent",
                    color: colors.primary,
                    border: `2px solid ${colors.primary}`,
                    boxShadow: "none",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.primary;
                    e.currentTarget.style.color = colors.white;
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(180, 132, 14, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = colors.primary;
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Se connecter
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showForgotPasswordModal && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowForgotPasswordModal(false);
              setResetEmail("");
              setResetSuccess(false);
              setError("");
            }
          }}
        >
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Mot de passe oublié ?</h2>

            {resetSuccess ? (
              <>
                <div style={styles.successMessage}>
                  <span style={styles.errorIcon}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </span>
                  <span>Email de réinitialisation envoyé avec succès !</span>
                </div>
                <p style={styles.modalText}>
                  Vérifiez votre boîte de réception à l'adresse{" "}
                  <strong>{resetEmail}</strong> et suivez les instructions pour
                  réinitialiser votre mot de passe.
                </p>
                <div style={styles.modalButtons}>
                  <button
                    style={{ ...styles.button, width: "100%" }}
                    onClick={() => {
                      setShowForgotPasswordModal(false);
                      setResetEmail("");
                      setResetSuccess(false);
                      setError("");
                    }}
                    onMouseEnter={(e) => {
                      Object.assign(e.currentTarget.style, styles.buttonHover);
                    }}
                    onMouseLeave={(e) => {
                      Object.assign(e.currentTarget.style, {
                        transform: "none",
                        boxShadow: "0 4px 12px rgba(180, 132, 14, 0.3)",
                      });
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={styles.modalText}>
                  Entrez votre adresse email et nous vous enverrons un lien pour
                  réinitialiser votre mot de passe.
                </p>

                {error && (
                  <div style={styles.error}>
                    <span style={styles.errorIcon}>
                      <FontAwesomeIcon icon={faExclamationCircle} />
                    </span>
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleForgotPassword}>
                  <div style={styles.inputGroup}>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      style={styles.input}
                      placeholder="votre@email.com"
                      required
                      autoFocus
                      onFocus={(e) =>
                        Object.assign(e.target.style, styles.inputFocus)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = colors.light;
                        e.target.style.backgroundColor =
                          "rgba(248, 249, 250, 0.8)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  <div style={styles.modalButtons}>
                    <button
                      type="button"
                      style={styles.cancelButton}
                      onClick={() => {
                        setShowForgotPasswordModal(false);
                        setResetEmail("");
                        setError("");
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.light;
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      style={{
                        ...styles.button,
                        opacity: resetLoading ? 0.7 : 1,
                      }}
                      disabled={resetLoading}
                      onMouseEnter={(e) => {
                        if (!resetLoading) {
                          Object.assign(
                            e.currentTarget.style,
                            styles.buttonHover
                          );
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!resetLoading) {
                          Object.assign(e.currentTarget.style, {
                            transform: "none",
                            boxShadow: "0 4px 12px rgba(180, 132, 14, 0.3)",
                          });
                        }
                      }}
                    >
                      {resetLoading ? (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "30px",
                              height: "16px",
                              border: "2px solid rgba(255,255,255,0.3)",
                              borderTop: "2px solid white",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          />
                          Envoi...
                        </span>
                      ) : (
                        "Confirmer"
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AuthPage;
